/* =============================================================================
 * Gigi Mix-Your-Flavours — pure state logic for the PDP buy-box mixer
 * =============================================================================
 * Framework-free, DOM-free. Shared verbatim by:
 *   - the buy box (sections/section-gigi-pdp-buy.liquid) via window.GigiMixer
 *   - the offline test suite (tests/mixer.mjs) via a default import
 * so the steppers the customer clicks and the multi-line payload sent to
 * /cart/add.js are the SAME logic that is unit-tested.
 *
 * The mixer splits a multi-tub subscription plan (3 Tubs Every 3 Months) across
 * the product's flavour variants. State is a plain serialisable object:
 *   { target: <tubs in the box>, qty: <number per variant, island order> }
 * Every operation is PURE: it returns a new state and never mutates its input.
 * `variants` is the buy box's parsed data island array (hidden flavours are
 * already filtered out of it, so they can never appear as a mixer row).
 *
 * UMD wrapper: the repo root is CommonJS (no "type":"module"), so this file
 * must resolve as CJS in Node while still exposing a global in the browser.
 * ===========================================================================*/
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api; // Node (CommonJS) — `import Mixer from '...'`
  } else {
    root.GigiMixer = api; // Browser global
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /** The mixer only engages on the 3-tub plan with at least two available flavours. */
  function isEligible(variants, plan) {
    if (!plan || plan.tubs !== 3 || !variants) return false;
    var avail = 0;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && variants[i].available) avail++;
    }
    return avail > 1;
  }

  /**
   * Fresh state: the VARIETY spread — `target` tubs dealt round-robin across
   * the available flavours, starting from the selected one (so with 3 flavours
   * and target 3 everyone gets 1; with 2 flavours the selected one gets the
   * remainder). Falls back to the first available flavour when the selected
   * one is unavailable. A prefill deliberately discards any custom mix.
   */
  function init(variants, selectedIndex, target) {
    var qty = [];
    for (var j = 0; j < variants.length; j++) qty.push(0);
    var order = [];
    var start = selectedIndex;
    if (!variants[start] || !variants[start].available) {
      start = -1;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i] && variants[i].available) { start = i; break; }
      }
    }
    if (start === -1) return { target: target, qty: qty };
    for (var k = 0; k < variants.length; k++) {
      var idx = (start + k) % variants.length;
      if (variants[idx] && variants[idx].available) order.push(idx);
    }
    for (var t = 0; t < target; t++) qty[order[t % order.length]] += 1;
    return { target: target, qty: qty };
  }

  function total(state) {
    var t = 0;
    for (var i = 0; i < state.qty.length; i++) t += state.qty[i];
    return t;
  }

  function remaining(state) { return state.target - total(state); }
  function canAdd(state) { return total(state) === state.target; }

  function canInc(state, i, variants) {
    return remaining(state) > 0 && !!(variants[i] && variants[i].available);
  }

  function canDec(state, i) { return state.qty[i] > 0; }

  function clone(state) { return { target: state.target, qty: state.qty.slice() }; }

  /** Clamped increment: a no-op clone when the box is full or the flavour unavailable. */
  function inc(state, i, variants) {
    var next = clone(state);
    if (canInc(state, i, variants)) next.qty[i] += 1;
    return next;
  }

  /** Clamped decrement: floors at zero. */
  function dec(state, i) {
    var next = clone(state);
    if (canDec(state, i)) next.qty[i] -= 1;
    return next;
  }

  /**
   * The /cart/add.js items array: one line per flavour with qty > 0, in island
   * order, every line carrying the SAME selling plan. Ids forced to Number —
   * Shopify's cart API rejects mixed types quietly in some themes.
   */
  function buildItems(state, variants, planId) {
    var items = [];
    for (var i = 0; i < state.qty.length; i++) {
      if (state.qty[i] > 0) {
        items.push({
          id: Number(variants[i].id),
          quantity: state.qty[i],
          selling_plan: Number(planId),
        });
      }
    }
    return items;
  }

  /** Σ qty_i × that variant's OWN plan price (plans can differ per variant). */
  function priceTotalCents(state, variants, planIndex) {
    var sum = 0;
    for (var i = 0; i < state.qty.length; i++) {
      if (state.qty[i] > 0) sum += state.qty[i] * variants[i].plans[planIndex].price;
    }
    return sum;
  }

  /** Σ qty_i × full (non-subscription) variant price, for the struck-through was-price. */
  function wasTotalCents(state, variants) {
    var sum = 0;
    for (var i = 0; i < state.qty.length; i++) {
      if (state.qty[i] > 0) sum += state.qty[i] * variants[i].price;
    }
    return sum;
  }

  /**
   * Format `cents` in the same shape as a Liquid-rendered money `sample`
   * ("€41.40", "41,40 kr", "£1,234.50"): find the numeric token, note whether
   * its decimal separator is a comma, and substitute the reformatted amount
   * back into the sample. Returns null when the sample has no usable numeric
   * token — the caller falls back to the section's own money() helper.
   */
  function formatMoneyLike(sample, cents) {
    if (!sample) return null;
    var m = String(sample).match(/\d[\d.,\s]*/);
    if (!m) return null;
    var token = m[0].replace(/\s+$/, '');
    // Decimal separator: the LAST . or , followed by exactly 2 digits at the end.
    var commaDecimal = /,\d{2}$/.test(token);
    var units = Math.floor(cents / 100);
    var minor = String(cents % 100);
    if (minor.length < 2) minor = '0' + minor;
    var amount = String(units) + (commaDecimal ? ',' : '.') + minor;
    return String(sample).replace(m[0], amount + (/\s$/.test(m[0]) ? ' ' : ''));
  }

  return {
    isEligible: isEligible,
    init: init,
    prefill: init,
    total: total,
    remaining: remaining,
    canAdd: canAdd,
    canInc: canInc,
    canDec: canDec,
    inc: inc,
    dec: dec,
    buildItems: buildItems,
    priceTotalCents: priceTotalCents,
    wasTotalCents: wasTotalCents,
    formatMoneyLike: formatMoneyLike,
  };
});
